import { Divider, Tooltip, TextField } from "@mui/material";
import dateformat from "dateformat";
import { useState, useEffect, useMemo } from "react";

import { Button, PreparationPDF, Text } from "$components";
import JTable from "$components/JTable/JTable";
import { queryClient } from "$utils/queryClient";
import { EDBService } from "$services";
import useAuth from "$hooks/useAuth";
import { useStyles as useEDBProfileClasses } from "$containers/EDB/styles/EdbProfile.Styles";
import useJStyles from "$hooks/useJStyles";
import { DispositionType, PreparationOrderStatus } from "$types";
import { DISPOSITION_LABELS } from "$utils/typeConstants";
import { DEFAULT_DATE_FORMAT } from "$utils/utils";
import { useProfileEDBContext } from "../context/ProfileEDBContext";
import InformationTable from "./InformationTable";

function DeliverySummary() {
  const { jclasses } = useJStyles();
  const edbClasses = useEDBProfileClasses();
  const { token } = useAuth();

  const { edb, opList, isDisabled, fetchEdb, integratedOrders } =
    useProfileEDBContext();

  const [analyticCodeValue, setAnalyticCodeValue] = useState(
    edb?.analyticCode || ""
  );

  useEffect(() => {
    setAnalyticCodeValue(edb?.analyticCode || "");
  }, [edb?.analyticCode]);

  const editAnalyticCode = analyticCodeValue !== (edb?.analyticCode || "");

  const saveAnalyticCode = async () => {
    try {
      await EDBService.UpdateEDB(
        token,
        { ...edb, analyticCode: analyticCodeValue, integratedOrders },
        edb._id
      );
      await queryClient.removeQueries({
        queryKey: ["getEdbById", edb._id],
      });
      await fetchEdb();
    } catch (e) {
      console.error("Erreur lors de la sauvegarde du code analytique", e);
    }
  };

  const getDispositionTypeLabel = (dispositionType: DispositionType) => {
    return DISPOSITION_LABELS[dispositionType] ?? null;
  };

  const columns = useMemo(
    () => [
      {
        id: "op_order",
        Header: "Ordre de Préparation",
        Cell: ({ row }: any) => {
          const op = row.original;
          const cancelled = op.status === PreparationOrderStatus.CANCELLED;
          const content = (
            <Text style={{ color: cancelled ? "#999" : "inherit" }}>
              {op.code || ""}
            </Text>
          );
          return cancelled ? (
            <Tooltip title="Cet ordre de préparation a été annulé" arrow>
              <div style={{ width: "100%", height: "100%" }}>{content}</div>
            </Tooltip>
          ) : (
            content
          );
        },
      },
      {
        id: "ref_op_warehouse",
        Header: "Ref Prépa Entrepôt",
        Cell: ({ row }: any) => {
          const op = row.original;
          const cancelled = op.status === PreparationOrderStatus.CANCELLED;
          const content = (
            <Text style={{ color: cancelled ? "#999" : "inherit" }}>
              {op.refPreparationEntrepot || "N/A"}
            </Text>
          );
          return cancelled ? (
            <Tooltip title="Cet ordre de préparation a été annulé" arrow>
              <div style={{ width: "100%", height: "100%" }}>{content}</div>
            </Tooltip>
          ) : (
            content
          );
        },
      },
      {
        id: "in_progress",
        Header: "Prépa. en cours",
        Cell: ({ row }: any) => {
          const op = row.original;
          const cancelled = op.status === PreparationOrderStatus.CANCELLED;
          const status = (op.statusHistory || []).find(
            (st: any) => st.status === PreparationOrderStatus.IN_PROGRESS
          );
          const hasDate =
            op.status >= PreparationOrderStatus.IN_PROGRESS && !!status;
          const content = (
            <Text
              style={{
                color: cancelled ? "#999" : "inherit",
                textDecoration: cancelled && hasDate ? "line-through" : "none",
              }}
            >
              {hasDate
                ? dateformat(status.statusDate, DEFAULT_DATE_FORMAT)
                : "-"}
            </Text>
          );
          return cancelled ? (
            <Tooltip title="Cet ordre de préparation a été annulé" arrow>
              <div style={{ width: "100%", height: "100%" }}>{content}</div>
            </Tooltip>
          ) : (
            content
          );
        },
      },
      {
        id: "dispo",
        Header: "Prépa. dispo",
        Cell: ({ row }: any) => {
          const op = row.original;
          const cancelled = op.status === PreparationOrderStatus.CANCELLED;
          const status = (op.statusHistory || []).find(
            (st: any) => st.status === PreparationOrderStatus.PREPARED
          );
          const hasDate =
            op.status >= PreparationOrderStatus.PREPARED && !!status;
          const content = (
            <Text
              style={{
                color: cancelled ? "#999" : "inherit",
                textDecoration: cancelled && hasDate ? "line-through" : "none",
              }}
            >
              {hasDate
                ? dateformat(status.statusDate, DEFAULT_DATE_FORMAT)
                : "-"}
            </Text>
          );
          return cancelled ? (
            <Tooltip title="Cet ordre de préparation a été annulé" arrow>
              <div style={{ width: "100%", height: "100%" }}>{content}</div>
            </Tooltip>
          ) : (
            content
          );
        },
      },
      {
        id: "expedition_in_progress",
        Header: "Expédié",
        Cell: ({ row }: any) => {
          const op = row.original;
          const cancelled = op.status === PreparationOrderStatus.CANCELLED;
          const status = (op.statusHistory || []).find(
            (st: any) => st.status === PreparationOrderStatus.SHIPPED
          );
          const hasDate =
            op.status >= PreparationOrderStatus.SHIPPED && !!status;
          const content = (
            <Text
              style={{
                color: cancelled ? "#999" : "inherit",
                textDecoration: cancelled && hasDate ? "line-through" : "none",
              }}
            >
              {hasDate
                ? dateformat(status.statusDate, DEFAULT_DATE_FORMAT)
                : "-"}
            </Text>
          );
          return cancelled ? (
            <Tooltip title="Cet ordre de préparation a été annulé" arrow>
              <div style={{ width: "100%", height: "100%" }}>{content}</div>
            </Tooltip>
          ) : (
            content
          );
        },
      },
      {
        id: "expedited",
        Header: "Livré",
        Cell: ({ row }: any) => {
          const op = row.original;
          const cancelled = op.status === PreparationOrderStatus.CANCELLED;
          const status = (op.statusHistory || []).find(
            (st: any) => st.status === PreparationOrderStatus.DELIVERED
          );
          const hasDate =
            op.status >= PreparationOrderStatus.DELIVERED && !!status;
          const content = (
            <Text
              style={{
                color: cancelled ? "#999" : "inherit",
                textDecoration: cancelled && hasDate ? "line-through" : "none",
              }}
            >
              {hasDate
                ? dateformat(status.statusDate, DEFAULT_DATE_FORMAT)
                : "-"}
            </Text>
          );
          return cancelled ? (
            <Tooltip title="Cet ordre de préparation a été annulé" arrow>
              <div style={{ width: "100%", height: "100%" }}>{content}</div>
            </Tooltip>
          ) : (
            content
          );
        },
      },
      {
        id: "shipped",
        Header: "Collecté",
        Cell: ({ row }: any) => {
          const op = row.original;
          const cancelled = op.status === PreparationOrderStatus.CANCELLED;
          const status = (op.statusHistory || []).find(
            (st: any) => st.status === PreparationOrderStatus.COLLECTED
          );
          const hasDate =
            op.status >= PreparationOrderStatus.COLLECTED && !!status;
          const content = (
            <Text
              style={{
                color: cancelled ? "#999" : "inherit",
                textDecoration: cancelled && hasDate ? "line-through" : "none",
              }}
            >
              {hasDate
                ? dateformat(status.statusDate, DEFAULT_DATE_FORMAT)
                : "-"}
            </Text>
          );
          return cancelled ? (
            <Tooltip title="Cet ordre de préparation a été annulé" arrow>
              <div style={{ width: "100%", height: "100%" }}>{content}</div>
            </Tooltip>
          ) : (
            content
          );
        },
      },
      {
        id: "action",
        Header: "Action",
        Cell: ({ row }: any) => {
          const op = row.original;
          const cancelled = op.status === PreparationOrderStatus.CANCELLED;
          const content = <PreparationPDF opData={op} disabled={cancelled} />;
          return cancelled ? (
            <Tooltip title="Cet ordre de préparation a été annulé" arrow>
              <div style={{ width: "100%", height: "100%" }}>{content}</div>
            </Tooltip>
          ) : (
            content
          );
        },
      },
    ],
    []
  );

  const preparationTypeRows = [
    {
      label: "Type de préparation souhaitée",
      value: edb?.preparationType,
    },
  ];

  const dispositionTypeRows = [
    {
      label: "Lieu de mise à disposition",
      value: getDispositionTypeLabel(edb?.dispositionType),
    },
  ];

  const contactRecoveryRows = [
    {
      label: "Contact",
      value: `${edb?.firstnameContactRecovery || "N/A"} ${
        edb?.lastnameContactRecovery?.toUpperCase() || "N/A"
      }`,
    },
    {
      label: "Email",
      value: edb?.emailContactRecovery || "N/A",
    },
    {
      label: "Téléphone",
      value: edb?.phoneContactRecovery || "N/A",
    },
  ];

  const dispositionAddressRows = [
    ...(edb?.dispositionType !== DispositionType.NEAREST_WAREHOUSE
      ? [
          ...([
            DispositionType.SUBCONTRACTED_WAREHOUSE,
            DispositionType.OTHER_PLACE,
            DispositionType.COLLECTED,
          ].includes(edb?.dispositionType)
            ? [
                {
                  label:
                    edb?.dispositionType === DispositionType.COLLECTED
                      ? "Entrepôt de préparation"
                      : "Nom entrepôt",
                  value: edb?.dispositionPlace || edb?.entrepotName || "N/A",
                },
              ]
            : []),
          {
            label: "Adresse",
            value: (
              <>
                {edb?.dispositionAdress}
                <br />
                {edb?.dispositionAdress2 && (
                  <>
                    {edb?.dispositionAdress2}
                    <br />
                  </>
                )}
                {edb?.dispositionPostalCode} {edb?.dispositionCity}
                <br />
                {edb?.dispositionCountry}
              </>
            ),
          },
        ]
      : []),
  ];

  // Updated to correctly display exact date or latest date label based on dispositionDateIsExact value
  const dispositionDateRows = [
    {
      label:
        edb?.dispositionDateIsExact === false
          ? "Au plus tard le"
          : "Date exacte",
      value:
        edb?.dispositionDate &&
        dateformat(new Date(edb?.dispositionDate), DEFAULT_DATE_FORMAT),
    },
  ];

  return (
    <div>
      <div className={edbClasses.informations}>
        <div className="about">
          <div className="left">
            <InformationTable rows={preparationTypeRows} />
          </div>
          <div className="right">
            <InformationTable rows={dispositionTypeRows} />
          </div>
        </div>
      </div>
      <Divider className={edbClasses.divider} />
      <div className={edbClasses.informations}>
        <div className="about">
          <div className="left">
            <Text type="h3" className={edbClasses.informationsTitle}>
              Contact de récupération du matériel
            </Text>
            <InformationTable rows={contactRecoveryRows} />
          </div>
          <div className="right">
            {edb?.dispositionType !== DispositionType.NEAREST_WAREHOUSE && (
              <>
                <Text type="h3" className={edbClasses.informationsTitle}>
                  {edb?.dispositionType === DispositionType.COLLECTED
                    ? "Entrepôt de préparation"
                    : "Adresse de mise à disposition"}
                </Text>
                <InformationTable rows={dispositionAddressRows} />
              </>
            )}
            <Text type="h3" className={edbClasses.informationsTitle}>
              Date de mise à disposition souhaitée
            </Text>
            <InformationTable rows={dispositionDateRows} />
          </div>
        </div>
        <Divider className={edbClasses.divider} />
        <div className={edbClasses.informations}>
          <Text type="h3" className={edbClasses.informationsTitle}>
            Code analytique
          </Text>
          <div
            className="about"
            style={{
              marginTop: 8,
              display: "flex",
              alignItems: "center",
              gap: 12,
            }}
          >
            <TextField
              style={{ backgroundColor: "white" }}
              value={analyticCodeValue}
              variant="outlined"
              placeholder="N/A"
              disabled={isDisabled}
              inputProps={{ maxLength: 50 }}
              size="small"
              onChange={(e) => setAnalyticCodeValue(e.target.value)}
            />
            {editAnalyticCode && (
              <Button onClick={saveAnalyticCode}>Enregistrer</Button>
            )}
          </div>
        </div>
        <Divider className={edbClasses.divider} />
        <div className={edbClasses.informations}>
          <Text type="h3" className={edbClasses.informationsTitle}>
            Notes, contraintes
          </Text>
          <div className="about" style={{ marginTop: 8 }}>
            {edb?.notes || "N/A"}
          </div>
        </div>
        <Divider className={edbClasses.divider} />
        <div className={jclasses.scrollTable}>
          <JTable
            data={opList || []}
            columns={columns}
            setPageIndex={() => {}}
            hideCheckboxes
            hideFilters
            rowsPerPage={1000}
            noDataText="Aucun ordre de préparation n'a encore été créé"
            usePaper={false}
            rowProps={() => ({ className: "nopointer" })}
          />
        </div>
      </div>
    </div>
  );
}

export default DeliverySummary;
